import React, { useState, useEffect } from "react";
import { AlertTriangle, Info, CheckCircle2, X } from "lucide-react";

export type ConfirmVariant = "danger" | "warning" | "info" | "success";

export interface ConfirmDialogOptions {
  title?: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  variant?: ConfirmVariant;
}

interface ActiveDialog extends ConfirmDialogOptions {
  resolve: (value: boolean) => void;
}

// ---------------------------------------------------------------------------
// Observable Confirm Dialog Store
// ---------------------------------------------------------------------------
let activeDialog: ActiveDialog | null = null;
const listeners = new Set<(dialog: ActiveDialog | null) => void>();

const emitChange = () => {
  listeners.forEach((listener) => listener(activeDialog));
};

export function confirmDialog(options: ConfirmDialogOptions): Promise<boolean> {
  return new Promise((resolve) => {
    activeDialog = {
      ...options,
      resolve: (confirmed: boolean) => {
        activeDialog = null;
        emitChange();
        resolve(confirmed);
      },
    };
    emitChange();
  });
}

// ---------------------------------------------------------------------------
// Global Confirm Dialog Container
// ---------------------------------------------------------------------------
export function ConfirmDialogContainer() {
  const [dialog, setDialog] = useState<ActiveDialog | null>(activeDialog);

  useEffect(() => {
    const handleChange = (newDialog: ActiveDialog | null) => {
      setDialog(newDialog);
    };
    listeners.add(handleChange);
    return () => {
      listeners.delete(handleChange);
    };
  }, []);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!dialog) return;
      if (e.key === "Escape") {
        dialog.resolve(false);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [dialog]);

  if (!dialog) return null;

  const variant = dialog.variant || "danger";

  const config = {
    danger: {
      icon: AlertTriangle,
      iconBg: "bg-rose-500/15 text-rose-400 border border-rose-500/30",
      btnConfirm:
        "bg-gradient-to-r from-rose-500 to-rose-600 hover:from-rose-600 hover:to-rose-700 text-white shadow-[0_4px_20px_-4px_rgba(244,63,94,0.4)]",
      defaultConfirmText: "Delete",
      defaultTitle: "Are you sure?",
    },
    warning: {
      icon: AlertTriangle,
      iconBg: "bg-amber-500/15 text-amber-400 border border-amber-500/30",
      btnConfirm:
        "bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white shadow-[0_4px_20px_-4px_rgba(245,158,11,0.4)]",
      defaultConfirmText: "Confirm",
      defaultTitle: "Confirmation Required",
    },
    info: {
      icon: Info,
      iconBg: "bg-cyan-500/15 text-cyan-400 border border-cyan-500/30",
      btnConfirm:
        "bg-gradient-to-r from-cyan-500 to-cyan-600 hover:from-cyan-600 hover:to-cyan-700 text-white shadow-[0_4px_20px_-4px_rgba(6,182,212,0.4)]",
      defaultConfirmText: "Proceed",
      defaultTitle: "Information",
    },
    success: {
      icon: CheckCircle2,
      iconBg: "bg-emerald-500/15 text-emerald-400 border border-emerald-500/30",
      btnConfirm:
        "bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white shadow-[0_4px_20px_-4px_rgba(16,185,129,0.4)]",
      defaultConfirmText: "Confirm",
      defaultTitle: "Success Action",
    },
  }[variant];

  const Icon = config.icon;

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-[100000] flex items-center justify-center bg-black/80 backdrop-blur-md p-4"
    >
      <div
        className="relative w-full max-w-md rounded-2xl border border-[rgba(247,247,248,0.14)] bg-[#131316] p-6 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={() => dialog.resolve(false)}
          className="absolute right-4 top-4 rounded-lg p-1.5 text-slate-400 hover:bg-white/[0.06] hover:text-white transition"
          aria-label="Cancel and close dialog"
        >
          <X className="h-4 w-4" />
        </button>

        <div className="flex items-start gap-4">
          <div className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${config.iconBg}`}>
            <Icon className="h-6 w-6" />
          </div>

          <div className="flex-1 pt-1">
            <h3 className="text-base font-bold text-white">
              {dialog.title || config.defaultTitle}
            </h3>
            <p className="mt-1.5 text-xs font-medium leading-relaxed text-[#94a3b8]">
              {dialog.message}
            </p>
          </div>
        </div>

        <div className="mt-6 flex items-center justify-end gap-3 pt-4 border-t border-[rgba(247,247,248,0.08)]">
          <button
            type="button"
            onClick={() => dialog.resolve(false)}
            className="rounded-xl border border-[rgba(247,247,248,0.12)] bg-[#1a1a20] px-4 py-2 text-xs font-semibold text-[#cbd5e1] hover:bg-white/[0.08] hover:text-white transition"
          >
            {dialog.cancelText || "Cancel"}
          </button>

          <button
            type="button"
            autoFocus
            onClick={() => dialog.resolve(true)}
            className={`rounded-xl px-4 py-2 text-xs font-bold transition duration-200 hover:brightness-110 active:scale-95 ${config.btnConfirm}`}
          >
            {dialog.confirmText || config.defaultConfirmText}
          </button>
        </div>
      </div>
    </div>
  );
}
